import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ButtonVariant } from '../../types/ButtonVariant';
import { Nullable } from '../../types/Nullable';
import { SharedButtonProps } from '../../types/SharedButtonProps';
import { Layout } from '../Layout/Layout';
import { Text } from '../Text/Text';
import { TextInput } from '../TextInput/TextInput';
import { useModal } from './useModal';

type Callback = () => void;

interface Props {
  children: React.ReactNode;
  confirmText: string;
  title: string;
  onDelete: () => void;
  isDeleting?: boolean;
  secondaryActions?: SharedButtonProps[];
}

export const useDeletePermanentModal = ({
  children,
  confirmText,
  isDeleting,
  secondaryActions,
  title,
  onDelete,
}: Props): [React.ReactNode, Callback, Callback] => {
  const intl = useIntl();
  const [text, setText] = useState<Nullable<string>>('');

  const contents = useMemo(
    () => (
      <Layout direction="column" spacing={4}>
        {children}
        <Text>
          <FormattedMessage
            id="UseDeletePermanentModal.confirmationMessage"
            defaultMessage={
              "You won't be able to revert this action. If you are sure, please type {confirmText} in the box below:"
            }
            description="Message to confirm permanent deletion of an item. Example: confirmText = 'sample text'"
            values={{
              confirmText,
            }}
          />
        </Text>
        <TextInput name="confirm-delete" value={text} onChange={setText} />
      </Layout>
    ),

    [children],
  );

  const disabled = text !== confirmText;

  const handleHide = () => setText('');

  const props = useMemo(
    () => ({
      children: contents,
      primaryAction: {
        disabled,
        isLoading: isDeleting,
        text: intl.formatMessage({
          id: 'UseDeletePermanentModal.deleteAction',
          defaultMessage: 'Permanently delete',
          description: 'Label for modal action to permanently delete',
        }),
        variant: 'danger' as ButtonVariant,
        onClick: onDelete,
      },

      secondaryActions,
      title,
      onHide: handleHide,
    }),

    [children, disabled, isDeleting, secondaryActions, title],
  );

  return useModal(props);
};
